/**
 * Codasc analytics — GA4 (gtag.js), shared by the Next platform shell and all
 * five Vite sub-apps so there is exactly one implementation of "send an event".
 *
 * Deliberately React-free and dependency-free, like `@codasc/pwa-install`: the
 * consumers are one SSR app and five SPAs, and the only thing they have in
 * common is a browser.
 *
 * FOUR RULES. These are the design, not decoration — they mirror
 * `platform/api/src/events/log.ts` rule 1, for the same reason:
 *
 *  1. **NEVER THROWS.** Every entry point is guarded. A tracking bug must never
 *     be able to take down a signup, a checkout, or an app launch.
 *  2. **NEVER BLOCKS.** `track()` is synchronous, returns void, and makes no
 *     request of its own — it hands the event to gtag, which ships it on
 *     `navigator.sendBeacon`. There is nothing to await, so no call site can
 *     put analytics between a user's click and its effect.
 *  3. **OFF IS THE DEFAULT PATH.** With no measurement id (local dev, staging)
 *     `initAnalytics` does nothing, `dataLayer` never exists, and every
 *     `track()` returns on its first guard. The disabled path is the one we
 *     exercise daily, so it stays honest.
 *  4. **A BLOCKED GA IS A NO-OP, NOT AN ERROR.** An ad blocker kills the
 *     gtag.js request, not the queue, so events pile into an array nobody
 *     drains. No throw, no retry, no user-visible difference.
 */
/**
 * GA4's own constraint: letters, digits and underscores, starting with a
 * letter, 40 chars max. Google silently drops an invalid name, so we drop it
 * here instead — an event that looks sent but never was is worse than one we
 * know we never had.
 */
const EVENT_NAME = /^[a-z][a-z0-9_]{0,39}$/;
/** Measurement ids and hostnames both come from this set; anything else could
 *  break out of the string it is interpolated into. */
const SAFE_TOKEN = /^[A-Za-z0-9.-]+$/;
export function isValidEventName(name) {
    return EVENT_NAME.test(name);
}
/**
 * Whole pesos from a PHP centavo amount, which is how the catalog and the
 * checkout rows store every price. GA4's `value` is a major-unit number, so
 * sending the raw centavo figure reports a ₱149 pass as ₱14,900.
 */
export function pesosFromCentavos(centavos) {
    if (!Number.isFinite(centavos))
        return 0;
    return Math.round(centavos) / 100;
}
/**
 * One-shot guard, module-scoped on purpose.
 *
 * A `useRef` would not survive React StrictMode's double-invoked effects in
 * dev, and a remount would re-fire besides. Keys are per-event so unrelated
 * events don't shadow each other.
 */
const fired = new Set();
export function once(key) {
    if (fired.has(key))
        return false;
    fired.add(key);
    return true;
}
/** Test seam — resets the `once()` ledger. Not used by app code. */
export function resetOnceForTests() {
    fired.clear();
}
function gtagWindow() {
    return typeof window === 'undefined' ? null : window;
}
/**
 * Send one GA4 event. Fire-and-forget: call it and move on, never await it,
 * never put it between a user's action and the navigation that follows.
 */
export function track(name, params) {
    try {
        const win = gtagWindow();
        if (!win)
            return; // SSR / prerender
        // GA disabled, or the bootstrap never ran. We deliberately do NOT create
        // the queue here: an array nobody drains is a leak, not analytics.
        if (!Array.isArray(win.dataLayer))
            return;
        if (!isValidEventName(name))
            return;
        if (typeof win.gtag !== 'function')
            return;
        win.gtag('event', name, params ?? {});
    }
    catch {
        // Telemetry is never worth an exception in a user's path. There is nothing
        // to retry and nothing to report — the event is simply lost.
    }
}
let started = false;
/**
 * Bootstrap GA4 in a client-rendered app (the Vite sub-apps).
 *
 * The Next shell does NOT call this — it renders the same shim server-side into
 * `<head>`, which it can do and an SPA cannot. Both end up with the identical
 * `dataLayer` + `gtag` contract, which is what lets `track()` be shared.
 *
 * Returns whether analytics actually started, for tests and callers that want
 * to log it. Never throws.
 */
export function initAnalytics(options = {}) {
    try {
        const { measurementId, linkerDomains } = options;
        if (!measurementId || !SAFE_TOKEN.test(measurementId))
            return false;
        const win = gtagWindow();
        if (!win || typeof document === 'undefined')
            return false;
        // Idempotent: a double-invoked effect or a re-imported module must not
        // load gtag.js twice or re-send `config`.
        if (started)
            return true;
        started = true;
        win.dataLayer ??= [];
        // Two things here are deliberate and easy to get wrong.
        //
        // It pushes `arguments`, not a rest array. Google's snippet has always
        // queued the arguments object, gtag.js reads the queue expecting that
        // shape, and the Next shell's inline shim does the same — so an array here
        // would feed the same library two different shapes depending on which
        // consumer bootstrapped it.
        //
        // And it resolves `window.dataLayer` per call rather than capturing it,
        // because gtag.js may swap the queue out from under us once it loads.
        //
        // Assigned rather than declared because a module has no global function
        // scope to hang `gtag` off — the one real difference from the snippet.
        win.gtag = function gtag() {
            window.dataLayer?.push(arguments);
        };
        const domains = (linkerDomains ?? []).filter((d) => SAFE_TOKEN.test(d));
        const config = domains.length > 1 ? { linker: { domains: [...domains] } } : {};
        win.gtag('js', new Date());
        win.gtag('config', measurementId, config);
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
        document.head.appendChild(script);
        return true;
    }
    catch {
        // A CSP, a blocked DOM, a sandboxed iframe — none of it is worth an
        // exception during app boot. The app runs; we just have no analytics.
        return false;
    }
}
/** Test seam — lets a test re-run `initAnalytics` from a clean slate. */
export function resetInitForTests() {
    started = false;
}
//# sourceMappingURL=index.js.map